import {useCallback} from 'react'
import {
  useNavigate as useRouterNavigate,
  parsePath,
  createPath,
  type NavigateOptions,
  type To,
} from 'react-router'
import {useInitialSearchParams} from '../../native/useInitialSearchParams'

function mergeSearchParams(preserved: URLSearchParams, callerSearch: string): string {
  const merged = new URLSearchParams(preserved)
  const callerParams = new URLSearchParams(callerSearch)
  callerParams.forEach((value, key) => {
    merged.set(key, value)
  })
  const result = merged.toString()
  return result ? `?${result}` : ''
}

export function appendPreservedParams(path: string, preserved: URLSearchParams): string {
  if (preserved.size === 0) {
    return path
  }

  const parsed = parsePath(path)
  const search = mergeSearchParams(preserved, (parsed.search ?? '').replace(/^\?/, ''))

  return createPath({...parsed, search})
}

/**
 * Drop-in replacement for react-router's useNavigate.
 *
 * Automatically appends preserved initial query params from Canvas
 * to every string-based navigation. This ensures params survive
 * client-side route transitions so that a page refresh sends
 * them back to the Canvas controller.
 *
 * - Numeric navigation (e.g., navigate(-1)) passes through unmodified.
 * - Caller-provided query params override preserved params on conflict.
 */

export function useNavigate() {
  const routerNavigate = useRouterNavigate()
  const {initialSearchParams} = useInitialSearchParams()

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        return routerNavigate(to)
      }

      if (typeof to === 'string') {
        return routerNavigate(appendPreservedParams(to, initialSearchParams), options)
      }

      // Object form ({ pathname, search, hash })
      const search = mergeSearchParams(initialSearchParams, (to.search ?? '').replace(/^\?/, ''))
      return routerNavigate({...to, search}, options)
    },
    [routerNavigate, initialSearchParams],
  )
}
